<script setup lang="ts">
import type { InsightSummaryCardModel } from '../../service/insightWorkbench'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Badge } from '@/shared/ui/badge'

defineProps<{ cards: readonly InsightSummaryCardModel[] }>()

const { t } = useI18n()
const format = useFormat()
</script>

<template>
  <section
    class="insight-summary grid grid-cols-4 gap-sm max-lg:grid-cols-2 max-[38rem]:grid-cols-[minmax(0,_1fr)]"
    :aria-label="t('insights.sysadmin.summary.label')"
  >
    <article
      v-for="card in cards"
      :key="card.outcome"
      class="insight-summary__card grid gap-sm p-md border border-border rounded-lg bg-surface"
    >
      <header>
        <span>{{ t(card.labelKey) }}</span>
        <strong>{{ format.formatNumber(card.count) }}</strong>
      </header>
      <progress :value="card.progress" max="100">
        {{ format.formatPercent(card.progress, { scale: 'percent', maxFrac: 0 }) }}
      </progress>
      <div
        class="insight-summary__severity flex min-h-6 flex-wrap gap-2xs text-muted text-[0.75rem]"
      >
        <Badge v-for="item in card.severity" :key="item.key" :tone="item.tone" size="sm">
          {{
            t('insights.sysadmin.summary.severityCount', {
              label: t(item.labelKey),
              count: format.formatNumber(item.count),
            })
          }}
        </Badge>
        <span v-if="card.severity.length === 0">{{ t('insights.sysadmin.summary.none') }}</span>
      </div>
    </article>
  </section>
</template>

<style scoped>
.insight-summary .insight-summary__card header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ui-space-sm);
  color: var(--ui-color-muted);
}
.insight-summary .insight-summary__card strong {
  color: var(--ui-color-fg);
  font-size: 1.5rem;
}
.insight-summary .insight-summary__card progress {
  width: 100%;
  accent-color: var(--ui-color-primary);
}
</style>
